// src/pages/clients/InstallationApproval.js

import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Chip,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Avatar,
    Badge,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Rating,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import {
    CheckCircle,
    Cancel,
    Schedule,
    Person,
    LocationOn,
    Phone,
    Email,
    Router,
    Speed,
    PhotoCamera,
    Build,
    ExpandMore,
    Info,
    Warning,
    Assignment,
    TrendingUp,
    NetworkWifi,
    Timer,
    Star,
    Refresh,
    Visibility,
    ThumbUp,
    ThumbDown,
    Edit
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

const InstallationApproval = () => {
    const [installations, setInstallations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedInstallation, setSelectedInstallation] = useState(null);
    const [editableEquipment, setEditableEquipment] = useState([]);
    const [detailDialog, setDetailDialog] = useState(false);
    const [actionDialog, setActionDialog] = useState(false);
    const [actionType, setActionType] = useState(''); // 'approve', 'reject', 'revision'
    const [actionNotes, setActionNotes] = useState('');
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    });

    useEffect(() => {
        if (selectedInstallation && selectedInstallation.completion_data?.equipment_installed) {
            const eqList = formatEquipment(selectedInstallation.completion_data.equipment_installed);
            setEditableEquipment(eqList.map(eq => ({
                ...eq,
                acquisition_type: eq.acquisition_type || 'rental',
                price: eq.price || eq.charged_amount || '0.00'
            })));
        } else {
            setEditableEquipment([]);
        }
    }, [selectedInstallation]);

    useEffect(() => {
        loadPendingInstallations();
    }, [pagination.page]);

    // Hook para inicializar y cargar dinámicamente Leaflet Map para la trayectoria
    useEffect(() => {
        if (!selectedInstallation || !detailDialog) return;
        
        let isMounted = true;
        
        const initLeafletMap = () => {
            const mapContainer = document.getElementById('trajectory-map');
            if (!mapContainer) return;
            
            if (!window.L) return;
            
            // Limpiar contenedor
            mapContainer.innerHTML = '';
            
            // Crear el div interno del mapa
            const mapDiv = document.createElement('div');
            mapDiv.style.width = '100%';
            mapDiv.style.height = '100%';
            mapContainer.appendChild(mapDiv);
            
            const L = window.L;
            
            // Obtener coordenadas de inicio, fin y planificadas
            const clientCoordsStr = selectedInstallation.installation.coordinates;
            const startCoordsStr = selectedInstallation.installation.start_coordinates;
            const finalCoordsStr = selectedInstallation.installation.final_coordinates;
            
            const parseLatLng = (str) => {
                if (!str) return null;
                const parts = str.split(',').map(p => parseFloat(p.trim()));
                if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                    return [parts[0], parts[1]]; // lat, lng
                }
                return null;
            };
            
            const clientLatLng = parseLatLng(clientCoordsStr);
            const startLatLng = parseLatLng(startCoordsStr);
            const finalLatLng = parseLatLng(finalCoordsStr);
            
            let center = [-12.046374, -77.042793];
            if (clientLatLng) center = clientLatLng;
            else if (finalLatLng) center = finalLatLng;
            else if (startLatLng) center = startLatLng;
            
            const osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; OpenStreetMap contributors',
                maxZoom: 22,
                maxNativeZoom: 19
            });

            const googleHybrid = L.tileLayer('https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
                subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
                attribution: '&copy; Google Maps',
                maxZoom: 22,
                maxNativeZoom: 20
            });

            const esriSatellite = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
                maxZoom: 22,
                maxNativeZoom: 17
            });

            const map = L.map(mapDiv, {
                maxZoom: 22,
                layers: [googleHybrid]
            }).setView(center, 14);

            const baseLayers = {
                "Mapa Estándar": osm,
                "Satelital Google (Híbrido)": googleHybrid,
                "Satélite Esri": esriSatellite
            };

            L.control.layers(baseLayers).addTo(map);
            
            const markers = [];
            
            // Marcador Cliente (Planificada)
            if (clientLatLng) {
                const clientIcon = L.icon({
                    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
                    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                    shadowSize: [41, 41]
                });
                L.marker(clientLatLng, { icon: clientIcon })
                    .bindPopup('🏠 <strong>Ubicación Planificada (Cliente)</strong><br/>' + (selectedInstallation.installation.address || ''))
                    .addTo(map);
                markers.push(clientLatLng);
            }
            
            // Marcador Inicio Técnico
            if (startLatLng) {
                const startIcon = L.icon({
                    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-yellow.png',
                    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                    shadowSize: [41, 41]
                });
                L.marker(startLatLng, { icon: startIcon })
                    .bindPopup('▶️ <strong>Inicio de Instalación</strong><br/>Coordenadas capturadas al iniciar')
                    .addTo(map);
                markers.push(startLatLng);
            }
            
            // Marcador Fin Técnico
            if (finalLatLng) {
                const finalIcon = L.icon({
                    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
                    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                    shadowSize: [41, 41]
                });
                L.marker(finalLatLng, { icon: finalIcon })
                    .bindPopup('🏁 <strong>Ubicación de Finalización</strong><br/>Coordenadas capturadas al completar')
                    .addTo(map);
                markers.push(finalLatLng);
            }
            
            // Recorrido (Breadcrumbs)
            const historyPoints = selectedInstallation.location_history || [];
            const historyLatLngs = historyPoints.map(p => [p.lat, p.lng]);
            
            if (historyLatLngs.length > 0) {
                L.polyline(historyLatLngs, { color: '#ff4d4d', weight: 4, opacity: 0.8, dashArray: '5, 10' }).addTo(map);
                
                historyPoints.forEach((point, idx) => {
                    const latlng = [point.lat, point.lng];
                    const time = point.recorded_at ? new Date(point.recorded_at).toLocaleTimeString('es-PE') : '';
                    L.circleMarker(latlng, {
                        radius: 6,
                        fillColor: '#ff1a1a',
                        color: '#ff9999',
                        weight: 2,
                        opacity: 0.9,
                        fillOpacity: 0.6
                    })
                    .bindPopup(`📍 <strong>Ubicación Reportada (${idx + 1})</strong><br/>Hora: ${time}`)
                    .addTo(map);
                    
                    markers.push(latlng);
                });
            }
            
            if (markers.length > 0) {
                const bounds = L.latLngBounds(markers);
                map.fitBounds(bounds, { padding: [50, 50] });
            }
        };

        if (!window.L) {
            if (!document.getElementById('leaflet-css')) {
                const link = document.createElement('link');
                link.id = 'leaflet-css';
                link.rel = 'stylesheet';
                link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
                document.head.appendChild(link);
            }
            
            if (!document.getElementById('leaflet-js')) {
                const script = document.createElement('script');
                script.id = 'leaflet-js';
                script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
                script.onload = () => {
                    if (isMounted) initLeafletMap();
                };
                document.body.appendChild(script);
            }
        } else {
            const timer = setTimeout(() => {
                if (isMounted) initLeafletMap();
            }, 200);
            return () => {
                isMounted = false;
                clearTimeout(timer);
            };
        }
        
        return () => {
            isMounted = false;
        };
    }, [selectedInstallation, detailDialog]);

    const loadPendingInstallations = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(
                `/api/installations/pending-approval?page=${pagination.page}&limit=${pagination.limit}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Error cargando instalaciones pendientes');
            }

            const data = await response.json();
            setInstallations(data.installations);
            setPagination(prev => ({
                ...prev,
                total: data.pagination.total,
                totalPages: data.pagination.totalPages
            }));
            setError(null);
        } catch (error) {
            console.error('Error loading pending installations:', error);
            setError('Error cargando instalaciones pendientes: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const loadInstallationDetails = async (installationId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `/api/installations/${installationId}/completion-data`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Error cargando detalles de instalación');
            }

            const data = await response.json();
            setSelectedInstallation(data);
            setDetailDialog(true);
        } catch (error) {
            console.error('Error loading installation details:', error);
            setError('Error cargando detalles: ' + error.message);
        }
    };

    const handleAction = async () => {
        if (!selectedInstallation || !actionType) return;

        if (actionType === 'reject' && (!actionNotes || actionNotes.trim() === '')) {
            setError('El motivo de rechazo es obligatorio');
            return;
        }

        try {
            setProcessing(true);
            const token = localStorage.getItem('token');

            let endpoint = '';
            let body = {};

            switch (actionType) {
                case 'approve':
                    endpoint = `/api/installations/${selectedInstallation.installation.id}/approve`;
                    body = { 
                        admin_notes: actionNotes,
                        equipment_installed: editableEquipment.map(eq => ({
                            ...eq,
                            price: parseFloat(eq.price) || 0.00
                        }))
                    };
                    break;
                case 'reject':
                    endpoint = `/api/installations/${selectedInstallation.installation.id}/reject`;
                    body = { rejection_reason: actionNotes, admin_notes: actionNotes };
                    break;
                case 'revision':
                    endpoint = `/api/installations/${selectedInstallation.installation.id}/request-revision`;
                    body = { revision_notes: actionNotes, admin_notes: actionNotes };
                    break;
                default:
                    throw new Error('Acción inválida');
            }

            const response = await fetch(`${endpoint}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error procesando acción');
            }

            const result = await response.json();

            // Mostrar mensaje de éxito
            let successMessage = result.message || '';
            if (!successMessage) {
                switch (actionType) {
                    case 'approve':
                        successMessage = 'Instalación aprobada exitosamente. Cliente creado.';
                        break;
                    case 'reject':
                        successMessage = 'Instalación rechazada. El técnico puede hacer correcciones.';
                        break;
                    case 'revision':
                        successMessage = 'Solicitud de revisión enviada al técnico.';
                        break;
                }
            }

            alert(successMessage);

            // Cerrar diálogos y recargar datos
            setActionDialog(false);
            setDetailDialog(false);
            setActionNotes('');
            setActionType('');
            await loadPendingInstallations();

        } catch (error) {
            console.error('Error processing action:', error);
            setError('Error procesando acción: ' + error.message);
        } finally {
            setProcessing(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'pending_rejection': return 'error';
            case 'approved': return 'success';
            case 'rejected': return 'error';
            case 'revision_required': return 'info';
            default: return 'default';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'pending': return 'Pendiente';
            case 'pending_rejection': return 'Rechazo Solicitado';
            case 'approved': return 'Aprobado';
            case 'rejected': return 'Rechazado';
            case 'revision_required': return 'Revisión Requerida';
            default: return status || 'Pendiente';
        }
    };

    const formatTestResults = (testResults) => {
        if (!testResults) return 'N/A';

        try {
            const results = typeof testResults === 'string' ? JSON.parse(testResults) : testResults;
            return (
                <Box>
                    <Typography variant="body2">📶 Descarga: {results.download_speed || 0} Mbps</Typography>
                    <Typography variant="body2">📤 Subida: {results.upload_speed || 0} Mbps</Typography>
                    <Typography variant="body2">⚡ Ping: {results.ping || 0} ms</Typography>
                    {results.signal_strength && (
                        <Typography variant="body2">📡 Señal: {results.signal_strength} dBm</Typography>
                    )}
                </Box>
            );
        } catch (e) {
            return 'Error en formato de datos';
        }
    };

    const formatEquipment = (equipment) => {
        if (!equipment) return [];

        try {
            return typeof equipment === 'string' ? JSON.parse(equipment) : equipment;
        } catch (e) {
            return [];
        }
    };

    const columns = [
        {
            field: 'client_name',
            headerName: 'Cliente',
            width: 200,
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" fontWeight="bold">
                        {params.row.first_name} {params.row.last_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {params.row.queue_name}
                    </Typography>
                </Box>
            )
        },
        {
            field: 'technician_name',
            headerName: 'Técnico',
            width: 150,
            renderCell: (params) => (
                <Box display="flex" alignItems="center">
                    <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main' }}>
                        <Person />
                    </Avatar>
                    <Typography variant="body2">{params.value}</Typography>
                </Box>
            )
        },
        {
            field: 'plan_name',
            headerName: 'Plan',
            width: 180,
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2">{params.value}</Typography>
                    <Typography variant="caption" color="primary">
                        S/. {params.row.plan_price} - {params.row.download_speed}/{params.row.upload_speed} Mbps
                    </Typography>
                </Box>
            )
        },
        {
            field: 'access_point_name',
            headerName: 'AP / Sectorial',
            width: 150,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ color: params.value ? 'text.primary' : 'text.disabled' }}>
                    {params.value || 'Sin asignar'}
                </Typography>
            )
        },
        {
            field: 'gps_verification_status',
            headerName: 'Auditoría GPS',
            width: 155,
            renderCell: (params) => {
                const status = params.row.gps_verification_status;
                const distance = params.row.gps_distance_meters;
                if (status === 'verified') {
                    return <Chip label={`Verificado (${distance}m)`} color="success" size="small" />;
                } else if (status === 'out_of_range') {
                    return <Chip label={`Fuera Rango (${distance}m)`} color="error" size="small" />;
                } else {
                    return <Chip label="Sin GPS" color="default" size="small" />;
                }
            }
        },
        {
            field: 'network_ping_status',
            headerName: 'MikroTik CPE',
            width: 145,
            renderCell: (params) => {
                const status = params.row.network_ping_status;
                const latency = params.row.network_latency_ms;
                if (status === 'online') {
                    return <Chip label={`Online (${latency}ms)`} color="success" size="small" />;
                } else if (status === 'offline') {
                    return <Chip label="Offline" color="error" size="small" />;
                } else if (status === 'router_unreachable') {
                    return <Chip label="Router no accesible" color="warning" size="small" />;
                } else {
                    return <Chip label="Sin probar" color="default" size="small" />;
                }
            }
        },
        {
            field: 'customer_satisfaction',
            headerName: 'Satisfacción',
            width: 120,
            renderCell: (params) => (
                params.value ? (
                    <Rating value={params.value} readOnly size="small" />
                ) : (
                    <Typography variant="caption">N/A</Typography>
                )
            )
        },
        {
            field: 'submitted_at',
            headerName: 'Enviado',
            width: 130,
            valueFormatter: (params) => {
                return new Date(params.value).toLocaleDateString('es-PE');
            }
        },
        {
            field: 'photo_count',
            headerName: 'Fotos',
            width: 80,
            renderCell: (params) => (
                <Badge badgeContent={params.value} color="primary">
                    <PhotoCamera />
                </Badge>
            )
        },
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 150,
            sortable: false,
            renderCell: (params) => (
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={() => loadInstallationDetails(params.row.id)}
                >
                    Ver Detalles
                </Button>
            )
        }
    ];

    if (loading && installations.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Cargando instalaciones pendientes...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            {/* Header */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 2
                }}>
                    <Box>
                        <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 'bold' }}>
                            Aprobación de Instalaciones
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {pagination.total} instalaciones pendientes de aprobación
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={loadPendingInstallations}
                        disabled={loading}
                        size="small"
                        sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                    >
                        Actualizar
                    </Button>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Lista de instalaciones */}
            {installations.length > 0 ? (
                <Paper sx={{ height: 600, width: '100%', overflowX: 'auto' }}>
                    <DataGrid
                        rows={installations}
                        columns={columns}
                        pageSize={pagination.limit}
                        rowsPerPageOptions={[10, 20, 50]}
                        disableSelectionOnClick
                        loading={loading}
                        pagination
                        paginationMode="server"
                        rowCount={pagination.total}
                        page={pagination.page - 1}
                        onPageChange={(page) => setPagination(prev => ({ ...prev, page: page + 1 }))}
                        density="comfortable"
                    />
                </Paper>
            ) : (
                <Alert severity="info">
                    No hay instalaciones pendientes de aprobación
                </Alert>
            )}

            {/* Dialog de detalles */}
            <Dialog
                open={detailDialog}
                onClose={() => setDetailDialog(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { minHeight: '80vh' } }}
            >
                {selectedInstallation && (
                    <>
                        <DialogTitle>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6">
                                    Detalles de Instalación - {selectedInstallation.installation.first_name} {selectedInstallation.installation.last_name}
                                </Typography>
                                <Chip
                                    label={getStatusText(selectedInstallation.completion_data?.approval_status)}
                                    color={getStatusColor(selectedInstallation.completion_data?.approval_status)}
                                />
                            </Box>
                        </DialogTitle>
                        <DialogContent dividers>
                            {selectedInstallation.completion_data?.approval_status === 'pending_rejection' && (
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2">El técnico ha solicitado rechazar y cancelar esta instalación.</Typography>
                                    <Typography variant="body2"><strong>Motivo:</strong> {selectedInstallation.completion_data?.rejection_reason || 'No especificado'}</Typography>
                                </Alert>
                            )}
                            <Grid container spacing={3}>
                                {/* Información del Cliente */}
                                <Grid item xs={12} md={6}>
                                    <Accordion defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Person sx={{ mr: 1 }} />
                                                Información del Cliente
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <List dense>
                                                <ListItem>
                                                    <ListItemIcon><Person /></ListItemIcon>
                                                    <ListItemText
                                                        primary="Nombre Completo"
                                                        secondary={`${selectedInstallation.installation.first_name} ${selectedInstallation.installation.last_name}`}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon><Phone /></ListItemIcon>
                                                    <ListItemText
                                                        primary="Teléfono"
                                                        secondary={selectedInstallation.installation.phone || 'No especificado'}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon><Email /></ListItemIcon>
                                                    <ListItemText
                                                        primary="Email"
                                                        secondary={selectedInstallation.installation.email || 'No especificado'}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon><LocationOn /></ListItemIcon>
                                                    <ListItemText
                                                        primary="Dirección"
                                                        secondary={selectedInstallation.installation.address}
                                                    />
                                                </ListItem>
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>

                                {/* Información Técnica */}
                                <Grid item xs={12} md={6}>
                                    <Accordion defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Router sx={{ mr: 1 }} />
                                                Información Técnica
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <List dense>
                                                <ListItem>
                                                    <ListItemIcon><NetworkWifi /></ListItemIcon>
                                                    <ListItemText
                                                        primary="Queue Name"
                                                        secondary={selectedInstallation.installation.queue_name}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon><Router /></ListItemIcon>
                                                    <ListItemText
                                                        primary="Dirección IP"
                                                        secondary={selectedInstallation.installation.ip_address}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon><Speed /></ListItemIcon>
                                                    <ListItemText
                                                        primary="Plan"
                                                        secondary={`${selectedInstallation.installation.plan_name} - ${selectedInstallation.installation.download_speed}/${selectedInstallation.installation.upload_speed} Mbps`}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon><LocationOn /></ListItemIcon>
                                                    <ListItemText
                                                        primary="Router"
                                                        secondary={`${selectedInstallation.installation.router_name} (${selectedInstallation.installation.router_ip})`}
                                                    />
                                                </ListItem>
                                                {selectedInstallation.installation.access_point_name && (
                                                    <ListItem>
                                                        <ListItemIcon><NetworkWifi /></ListItemIcon>
                                                        <ListItemText
                                                            primary="AP / Sectorial"
                                                            secondary={`${selectedInstallation.installation.access_point_name}${selectedInstallation.installation.access_point_ip ? ` (${selectedInstallation.installation.access_point_ip})` : ''}${selectedInstallation.installation.ssid ? ` - SSID: ${selectedInstallation.installation.ssid}` : ''}`}
                                                        />
                                                    </ListItem>
                                                )}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>

                                {/* Datos del Técnico */}
                                <Grid item xs={12}>
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Build sx={{ mr: 1 }} />
                                                Datos Reportados por el Técnico
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" gutterBottom>Notas de Instalación:</Typography>
                                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                                        {selectedInstallation.completion_data?.installation_notes || 'Sin notas'}
                                                    </Typography>

                                                    <Typography variant="subtitle2" gutterBottom>Satisfacción del Cliente:</Typography>
                                                    <Box sx={{ mb: 2 }}>
                                                        {selectedInstallation.completion_data?.customer_satisfaction ? (
                                                            <Rating
                                                                value={selectedInstallation.completion_data.customer_satisfaction}
                                                                readOnly
                                                            />
                                                        ) : (
                                                            <Typography variant="body2">No calificado</Typography>
                                                        )}
                                                    </Box>

                                                    {selectedInstallation.completion_data?.total_duration_minutes && (
                                                        <>
                                                            <Typography variant="subtitle2" gutterBottom>Duración:</Typography>
                                                            <Typography variant="body2" sx={{ mb: 2 }}>
                                                                {Math.floor(selectedInstallation.completion_data.total_duration_minutes / 60)}h {selectedInstallation.completion_data.total_duration_minutes % 60}m
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" gutterBottom>Resultados de Pruebas:</Typography>
                                                    <Box sx={{ mb: 2 }}>
                                                        {formatTestResults(selectedInstallation.completion_data?.test_results)}
                                                    </Box>

                                                    {selectedInstallation.completion_data?.signal_strength_dbm && (
                                                        <>
                                                            <Typography variant="subtitle2" gutterBottom>Intensidad de Señal:</Typography>
                                                            <Typography variant="body2" sx={{ mb: 2 }}>
                                                                {selectedInstallation.completion_data.signal_strength_dbm} dBm
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Grid>

                                                {selectedInstallation.completion_data?.issues_encountered && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="subtitle2" gutterBottom color="warning.main">
                                                            Problemas Encontrados:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ mb: 2 }}>
                                                            {selectedInstallation.completion_data.issues_encountered}
                                                        </Typography>
                                                    </Grid>
                                                )}

                                                {selectedInstallation.completion_data?.recommendations && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="subtitle2" gutterBottom color="info.main">
                                                            Recomendaciones:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ mb: 2 }}>
                                                            {selectedInstallation.completion_data.recommendations}
                                                        </Typography>
                                                    </Grid>
                                                )}

                                                {/* Equipos Instalados */}
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2" gutterBottom>Equipos Instalados (Términos Comerciales Editables):</Typography>
                                                    {editableEquipment.length > 0 ? (
                                                        <TableContainer component={Paper} variant="outlined">
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>Equipo</TableCell>
                                                                        <TableCell>Serie</TableCell>
                                                                        <TableCell>MAC</TableCell>
                                                                        <TableCell>Modo Adquisición</TableCell>
                                                                        <TableCell>Precio Cobrado (S/)</TableCell>
                                                                        <TableCell>Notas</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {editableEquipment.map((eq, index) => (
                                                                        <TableRow key={index}>
                                                                            <TableCell>{eq.item_name || 'N/A'}</TableCell>
                                                                            <TableCell>{eq.serial_number || 'N/A'}</TableCell>
                                                                            <TableCell>{eq.mac_address || 'N/A'}</TableCell>
                                                                            <TableCell>
                                                                                <TextField
                                                                                    select
                                                                                    size="small"
                                                                                    value={eq.acquisition_type || 'rental'}
                                                                                    onChange={(e) => {
                                                                                        const val = e.target.value;
                                                                                        setEditableEquipment(prev => prev.map((item, idx) => 
                                                                                            idx === index ? { ...item, acquisition_type: val, price: val === 'none' ? '0.00' : item.price } : item
                                                                                        ));
                                                                                    }}
                                                                                    sx={{ minWidth: 150 }}
                                                                                >
                                                                                    <MenuItem value="rental">Alquiler / Garantía</MenuItem>
                                                                                    <MenuItem value="sale">Venta</MenuItem>
                                                                                    <MenuItem value="none">Ninguno</MenuItem>
                                                                                </TextField>
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <TextField
                                                                                    type="number"
                                                                                    size="small"
                                                                                    value={eq.price || '0.00'}
                                                                                    onChange={(e) => {
                                                                                        const val = e.target.value;
                                                                                        setEditableEquipment(prev => prev.map((item, idx) => 
                                                                                            idx === index ? { ...item, price: val } : item
                                                                                        ));
                                                                                    }}
                                                                                    inputProps={{ min: 0, step: 0.01 }}
                                                                                    disabled={eq.acquisition_type === 'none'}
                                                                                    sx={{ width: 100 }}
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell>{eq.notes || 'N/A'}</TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    ) : (
                                                        <Typography variant="body2">No se reportaron equipos</Typography>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>

                                {/* Panel de Auditoría de Veracidad (5 Capas) */}
                                <Grid item xs={12}>
                                    <Accordion defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 'bold' }}>
                                                <Info sx={{ mr: 1 }} />
                                                Panel de Auditoría de Veracidad (5 Capas)
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={3}>
                                                {/* Capa 1: Auditoría GPS */}
                                                <Grid item xs={12} md={6}>
                                                    <Paper sx={{ p: 2, height: '100%', border: '1px solid', borderColor: 'divider' }}>
                                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LocationOn color="primary" /> Capa 1: Auditoría GPS
                                                        </Typography>
                                                        <Divider sx={{ mb: 2 }} />
                                                        <Box sx={{ mb: 2 }}>
                                                            <Typography variant="body2" color="textSecondary">Ubicación Planificada (Catastro):</Typography>
                                                            <Typography variant="body1" fontWeight="medium">
                                                                {selectedInstallation.installation.coordinates || 'No registrada'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ mb: 2 }}>
                                                            <Typography variant="body2" color="textSecondary">Ubicación de Inicio (Técnico):</Typography>
                                                            <Typography variant="body1" fontWeight="medium">
                                                                {selectedInstallation.installation.start_coordinates || 'No registrada'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ mb: 2 }}>
                                                            <Typography variant="body2" color="textSecondary">Ubicación de Finalización (Técnico):</Typography>
                                                            <Typography variant="body1" fontWeight="medium">
                                                                {selectedInstallation.installation.final_coordinates || 'No registrada'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                                                            <Typography variant="body2" color="textSecondary">Distancia Calculada:</Typography>
                                                            <Chip 
                                                                label={selectedInstallation.completion_data?.gps_distance_meters !== null ? `${selectedInstallation.completion_data.gps_distance_meters} metros` : 'N/A'}
                                                                color={selectedInstallation.completion_data?.gps_verification_status === 'verified' ? 'success' : 'error'}
                                                                size="small"
                                                            />
                                                            <Chip 
                                                                label={
                                                                    selectedInstallation.completion_data?.gps_verification_status === 'verified' ? 'Verificado (<100m)' :
                                                                    selectedInstallation.completion_data?.gps_verification_status === 'out_of_range' ? 'Fuera de Rango' : 'Sin datos'
                                                                }
                                                                color={selectedInstallation.completion_data?.gps_verification_status === 'verified' ? 'success' : 'error'}
                                                                size="small"
                                                            />
                                                        </Box>
                                                        {selectedInstallation.installation.coordinates && selectedInstallation.installation.final_coordinates && (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                startIcon={<LocationOn />}
                                                                href={`https://www.google.com/maps/dir/${selectedInstallation.installation.coordinates.replace(/\s+/g, '')}/${selectedInstallation.installation.final_coordinates.replace(/\s+/g, '')}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                sx={{ mt: 1 }}
                                                            >
                                                                Comparar en Google Maps
                                                            </Button>
                                                        )}
                                                    </Paper>
                                                </Grid>

                                                {/* Capa 3 y 4: Auditoría MikroTik */}
                                                <Grid item xs={12} md={6}>
                                                    <Paper sx={{ p: 2, height: '100%', border: '1px solid', borderColor: 'divider' }}>
                                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Router color="primary" /> Capas 3 y 4: Auditoría MikroTik (Red y CPE)
                                                        </Typography>
                                                        <Divider sx={{ mb: 2 }} />
                                                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body2">Prueba de Ping CPE:</Typography>
                                                            <Chip 
                                                                label={
                                                                    selectedInstallation.completion_data?.network_ping_status === 'online' ? `Online (${selectedInstallation.completion_data.network_latency_ms} ms)` :
                                                                    selectedInstallation.completion_data?.network_ping_status === 'offline' ? 'Offline' :
                                                                    selectedInstallation.completion_data?.network_ping_status === 'router_unreachable' ? 'Router no accesible' : 'Sin probar'
                                                                }
                                                                color={
                                                                    selectedInstallation.completion_data?.network_ping_status === 'online' ? 'success' :
                                                                    selectedInstallation.completion_data?.network_ping_status === 'router_unreachable' ? 'warning' : 'error'
                                                                }
                                                                size="small"
                                                            />
                                                        </Box>
                                                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body2">Verificación de Puerto ARP (MAC física):</Typography>
                                                            <Chip 
                                                                label={selectedInstallation.completion_data?.network_arp_verified ? 'Verificado (MAC coincide)' : 'No verificado'}
                                                                color={selectedInstallation.completion_data?.network_arp_verified ? 'success' : 'error'}
                                                                size="small"
                                                            />
                                                        </Box>
                                                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body2">Estado de Auditoría de Red:</Typography>
                                                            <Chip 
                                                                label={
                                                                    selectedInstallation.completion_data?.network_verification_status === 'verified' ? 'Red Verificada' :
                                                                    selectedInstallation.completion_data?.network_verification_status === 'router_unreachable' ? 'Router no accesible' : 'Sin Verificar'
                                                                }
                                                                color={
                                                                    selectedInstallation.completion_data?.network_verification_status === 'verified' ? 'success' :
                                                                    selectedInstallation.completion_data?.network_verification_status === 'router_unreachable' ? 'warning' : 'error'
                                                                }
                                                                size="small"
                                                            />
                                                        </Box>
                                                    </Paper>
                                                </Grid>

                                                {/* Capa 2: Auditoría EXIF (Metadatos de Foto) */}
                                                <Grid item xs={12}>
                                                    <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <PhotoCamera color="primary" /> Capa 2: Auditoría EXIF de Fotos de Evidencia
                                                        </Typography>
                                                        <Divider sx={{ mb: 2 }} />
                                                        <Grid container spacing={2}>
                                                            {selectedInstallation.photos.map((photo) => (
                                                                <Grid item xs={12} sm={6} md={3} key={photo.id}>
                                                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                                                        <img
                                                                            src={photo.photo_url}
                                                                            alt={photo.caption}
                                                                            style={{ width: '100%', height: '120px', objectFit: 'cover' }}
                                                                        />
                                                                        <CardContent sx={{ p: 1.5 }}>
                                                                            <Typography variant="subtitle2" noWrap>{photo.photo_type}</Typography>
                                                                            <Typography variant="caption" display="block" color="textSecondary" noWrap>
                                                                                {photo.caption || 'Sin descripción'}
                                                                            </Typography>
                                                                            <Divider sx={{ my: 1 }} />
                                                                            {photo.exif_latitude ? (
                                                                                <Box>
                                                                                    <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>EXIF Satélite:</Typography>
                                                                                    <Typography variant="caption" display="block" color="primary">
                                                                                        {photo.exif_latitude}, {photo.exif_longitude}
                                                                                    </Typography>
                                                                                    <Typography variant="caption" display="block">Captura: {new Date(photo.exif_captured_at).toLocaleString()}</Typography>
                                                                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                                                        Distancia: <strong>{photo.exif_distance_meters}m</strong>
                                                                                    </Typography>
                                                                                    <Chip 
                                                                                        label={
                                                                                            photo.exif_verification_status === 'verified' ? 'Verificado' :
                                                                                            photo.exif_verification_status === 'out_of_range' ? 'Fuera Rango' : photo.exif_verification_status
                                                                                        }
                                                                                        color={photo.exif_verification_status === 'verified' ? 'success' : 'error'}
                                                                                        size="small"
                                                                                        sx={{ mt: 1, height: '20px', fontSize: '10px' }}
                                                                                    />
                                                                                </Box>
                                                                            ) : (
                                                                                <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', display: 'block', mt: 1 }}>
                                                                                    ⚠️ Sin metadatos EXIF geográficos en la foto
                                                                                </Typography>
                                                                            )}
                                                                        </CardContent>
                                                                    </Card>
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    </Paper>
                                                </Grid>

                                                {/* Capa 5: Firma de Cliente */}
                                                <Grid item xs={12} md={6}>
                                                    <Paper sx={{ p: 2, height: '100%', border: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
                                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Assignment color="primary" /> Capa 5: Firma del Cliente
                                                        </Typography>
                                                        <Divider sx={{ mb: 2 }} />
                                                        {selectedInstallation.completion_data?.client_signature_url ? (
                                                            <Box sx={{ 
                                                                bgcolor: '#fff', 
                                                                border: '1px solid #ccc', 
                                                                borderRadius: 1, 
                                                                p: 2, 
                                                                display: 'flex', 
                                                                justifyContent: 'center', 
                                                                alignItems: 'center',
                                                                flexGrow: 1,
                                                                minHeight: '150px'
                                                            }}>
                                                                <img
                                                                    src={selectedInstallation.completion_data.client_signature_url}
                                                                    alt="Firma del cliente"
                                                                    style={{ maxHeight: '120px', maxWidth: '100%', objectFit: 'contain' }}
                                                                />
                                                            </Box>
                                                        ) : (
                                                            <Box sx={{ 
                                                                bgcolor: 'action.hover', 
                                                                border: '1px dashed', 
                                                                borderColor: 'divider',
                                                                borderRadius: 1, 
                                                                p: 4, 
                                                                textAlign: 'center',
                                                                flexGrow: 1,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}>
                                                                <Typography variant="body2" color="textSecondary">No se cargó firma digital</Typography>
                                                            </Box>
                                                        )}
                                                    </Paper>
                                                </Grid>

                                                {/* Capa 5: Historial de Ubicación del Técnico (Breadcrumbs) */}
                                                <Grid item xs={12} md={6}>
                                                    <Paper sx={{ p: 2, height: '100%', border: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
                                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Timer color="primary" /> Capa 5: Trayectoria de Geolocalización (Técnico)
                                                        </Typography>
                                                        <Divider sx={{ mb: 2 }} />
                                                        
                                                        {/* Contenedor del Mapa Leaflet */}
                                                        <Box 
                                                            id="trajectory-map" 
                                                            sx={{ 
                                                                width: '100%', 
                                                                height: '250px', 
                                                                bgcolor: 'action.hover', 
                                                                borderRadius: 1, 
                                                                mb: 2,
                                                                overflow: 'hidden',
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                zIndex: 1
                                                            }}
                                                        />
                                                        
                                                        {selectedInstallation.location_history?.length > 0 ? (
                                                            <>
                                                                <Typography variant="subtitle2" gutterBottom>Línea de Tiempo del Recorrido:</Typography>
                                                                <Box sx={{ overflowY: 'auto', maxHeight: '150px', mb: 2, flexGrow: 1, pr: 1 }}>
                                                                    {selectedInstallation.location_history.map((point, index) => (
                                                                        <Box key={index} sx={{ 
                                                                            display: 'flex', 
                                                                            alignItems: 'flex-start', 
                                                                            mb: 1.5,
                                                                            position: 'relative',
                                                                            pl: 3
                                                                        }}>
                                                                            {/* Bola de la línea de tiempo */}
                                                                            <Box sx={{
                                                                                position: 'absolute',
                                                                                left: 4,
                                                                                top: 4,
                                                                                width: 10,
                                                                                height: 10,
                                                                                borderRadius: '50%',
                                                                                bgcolor: 'primary.main',
                                                                                zIndex: 2
                                                                            }} />
                                                                            {/* Línea conectora */}
                                                                            {index < selectedInstallation.location_history.length - 1 && (
                                                                                <Box sx={{
                                                                                    position: 'absolute',
                                                                                    left: 8,
                                                                                    top: 14,
                                                                                    bottom: -14,
                                                                                    width: 2,
                                                                                    bgcolor: 'divider',
                                                                                    zIndex: 1
                                                                                }} />
                                                                            )}
                                                                            <Box>
                                                                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium' }}>
                                                                                    {new Date(point.recorded_at).toLocaleTimeString('es-PE')}
                                                                                </Typography>
                                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                                                                    {point.lat}, {point.lng}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    startIcon={<LocationOn />}
                                                                    href={`https://www.google.com/maps/dir/${selectedInstallation.location_history.map(p => `${p.lat},${p.lng}`).join('/')}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    fullWidth
                                                                >
                                                                    Ver Trayectoria de Viaje Completa
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'action.hover', borderRadius: 1 }}>
                                                                <Typography variant="body2" color="textSecondary">
                                                                    ⚠️ No hay registros de rastro GPS de fondo. El técnico no activó su jornada o no mantuvo el portal abierto durante el viaje.
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Paper>
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>

                                {/* Fotos */}
                                {selectedInstallation.photos.length > 0 && (
                                    <Grid item xs={12}>
                                        <Accordion>
                                            <AccordionSummary expandIcon={<ExpandMore />}>
                                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <PhotoCamera sx={{ mr: 1 }} />
                                                    Fotos de Instalación ({selectedInstallation.photos.length})
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Grid container spacing={2}>
                                                    {selectedInstallation.photos.map((photo) => (
                                                        <Grid item xs={6} md={3} key={photo.id}>
                                                            <Card>
                                                                <img
                                                                    src={photo.photo_url}
                                                                    alt={photo.caption}
                                                                    style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                                                                />
                                                                <CardContent sx={{ p: 1 }}>
                                                                    <Typography variant="caption">
                                                                        {photo.photo_type} - {photo.caption}
                                                                    </Typography>
                                                                </CardContent>
                                                            </Card>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Grid>
                                )}
                            </Grid>
                        </DialogContent>
                        <DialogActions sx={{ p: 2, gap: 1 }}>
                            <Button
                                onClick={() => setDetailDialog(false)}
                                variant="outlined"
                            >
                                Cerrar
                            </Button>
                            
                            {selectedInstallation.completion_data?.approval_status !== 'pending_rejection' && (
                                <>
                                    <Button
                                        onClick={() => {
                                            setActionType('revision');
                                            setActionDialog(true);
                                        }}
                                        variant="outlined"
                                        color="info"
                                        startIcon={<Edit />}
                                    >
                                        Solicitar Revisión
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setActionType('reject');
                                            setActionDialog(true);
                                        }}
                                        variant="outlined"
                                        color="error"
                                        startIcon={<ThumbDown />}
                                    >
                                        Rechazar
                                    </Button>
                                </>
                            )}
                            
                            <Button
                                onClick={() => {
                                    setActionType('approve');
                                    setActionDialog(true);
                                }}
                                variant="contained"
                                color={selectedInstallation.completion_data?.approval_status === 'pending_rejection' ? 'error' : 'success'}
                                startIcon={selectedInstallation.completion_data?.approval_status === 'pending_rejection' ? <Cancel /> : <ThumbUp />}
                            >
                                {selectedInstallation.completion_data?.approval_status === 'pending_rejection' ? 'Aceptar Rechazo' : 'Aprobar'}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Dialog de acción */}
            <Dialog open={actionDialog} onClose={() => setActionDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {actionType === 'approve' && selectedInstallation?.completion_data?.approval_status === 'pending_rejection' ? 'Aceptar Rechazo de Instalación' :
                     actionType === 'approve' ? 'Aprobar Instalación' :
                     actionType === 'reject' ? 'Rechazar Instalación' :
                     actionType === 'revision' ? 'Solicitar Revisión' : ''}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label={
                            actionType === 'approve' && selectedInstallation?.completion_data?.approval_status === 'pending_rejection' ? 'Notas de cancelación (opcional)' :
                            actionType === 'approve' ? 'Notas de aprobación (opcional)' :
                            actionType === 'reject' ? 'Motivo de rechazo *' : 'Comentarios para revisión'
                        }
                        value={actionNotes}
                        onChange={(e) => setActionNotes(e.target.value)}
                        required={actionType === 'reject'}
                        placeholder={
                            actionType === 'approve' && selectedInstallation?.completion_data?.approval_status === 'pending_rejection' ? 'Añadir notas sobre la cancelación...' :
                            actionType === 'approve' ? 'Instalación aprobada sin observaciones...' :
                            actionType === 'reject' ? 'Especifique el motivo del rechazo...' :
                            'Indique qué debe revisar el técnico...'
                        }
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setActionDialog(false)} disabled={processing}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleAction}
                        variant="contained"
                        color={
                            actionType === 'approve' ? 'success' :
                                actionType === 'reject' ? 'error' : 'info'
                        }
                        disabled={processing || (actionType === 'reject' && !actionNotes.trim())}
                        startIcon={processing ? <CircularProgress size={20} /> : null}
                    >
                        {processing ? 'Procesando...' :
                            actionType === 'approve' && selectedInstallation?.completion_data?.approval_status === 'pending_rejection' ? 'Confirmar Cancelación' :
                            actionType === 'approve' ? 'Aprobar' :
                                actionType === 'reject' ? 'Rechazar' : 'Enviar Revisión'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InstallationApproval;